import React from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { Music, BookOpen, Video, Download, Play, ArrowRight, Headphones } from 'lucide-react';
import CorporateHeader from './CorporateHeader';
import Footer from './Footer';

const MindsetShiftLandingPage = () => {
  const navigate = useNavigate();

  const products = [
    {
      title: 'The Official Soundtrack',
      description: '11 transformational R&B tracks + Bonus Lyric Sheet PDF. Available now for instant download.',
      price: '$9.99',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png',
      cta: 'Purchase Soundtrack',
      path: '/mindset-shift-soundtrack',
      streamLink: 'https://distrokid.com/hyperfollow/egyptyoung/mindset-shift-the-official-soundtrack-2',
      available: true,
    },
    {
      title: 'The Mindset Shift Book',
      description: 'The foundational text that started the movement. Transform your thinking, transform your life.',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/pjhwtif3_THE%20MINDSET%20SHIFT%20-%20cover.png',
      cta: 'Coming Soon',
      available: false,
    },
    {
      title: 'True Companion Journal',
      description: 'Your personal growth companion. Guided journaling for daily mindset shifts.',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/fap9i5s3_MINDSET%20SHIFT%20TRUE%20COMPANION%20JOURNAL.png',
      cta: 'Coming Soon',
      available: false,
    },
    {
      title: 'Book of Daily Affirmations',
      description: 'Daily affirmations to reprogram your mindset and build confidence.',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/y4rkhenp_MINDSET%20SHIFT%20BOOK%20OF%20DAILY%20AFFIRMATIONS.png',
      cta: 'Coming Soon',
      available: false,
    },
    {
      title: 'Mindset Shift Workbook',
      description: 'Interactive exercises to reinforce your transformation.',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/dmdu0u7i_MINDSET%20SHIFT%20WORKBOOK%20COVER.png',
      cta: 'Coming Soon',
      available: false,
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* Hero */}
      <section className="pt-28 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/30 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <div className="inline-flex items-center gap-2 text-purple-400 text-sm font-semibold mb-4">
                <Music className="w-4 h-4" />
                MINDSET SHIFT
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4" data-testid="mindsetshift-title">
                Mindset <span className="text-purple-400">Shift</span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed mb-8">
                Transformational media for individuals and organizations. Music, books, video content, and corporate licensing designed to shift perspectives and unlock potential.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => navigate('/mindset-shift-soundtrack')} className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-3">
                  <Download className="w-5 h-5 mr-2" />
                  Get the Soundtrack — $9.99
                </Button>
                <a href="https://distrokid.com/hyperfollow/egyptyoung/mindset-shift-the-official-soundtrack-2" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white">
                    <Play className="w-5 h-5 mr-2" />
                    Stream Now
                  </Button>
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png"
                alt="Mindset Shift Soundtrack"
                className="w-80 lg:w-96 rounded-xl shadow-2xl shadow-purple-900/40"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">The Collection</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {products.map((product) => (
              <div key={product.title} className={`bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden transition-all duration-300 hover:border-purple-600/40 ${!product.available ? 'opacity-70' : ''}`}>
                <div className="aspect-square">
                  <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{product.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    {product.price && <span className="text-purple-400 font-bold text-xl">{product.price}</span>}
                    {product.available ? (
                      <div className="flex gap-2">
                        <Button onClick={() => navigate(product.path)} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
                          {product.cta}
                        </Button>
                        {product.streamLink && (
                          <a href={product.streamLink} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="border-purple-600 text-purple-400">
                              <Play className="w-4 h-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    ) : (
                      <Button disabled size="sm" className="bg-gray-700 text-gray-400 cursor-not-allowed">Coming Soon</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jersey Mindset Auntie + Corporate Licensing */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 hover:border-purple-600/40 transition-all">
              <Video className="w-10 h-10 text-purple-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Jersey Mindset Auntie</h3>
              <p className="text-gray-400 mb-4">Daily videos that reinforce the mindset shifts you need to create real opportunity.</p>
              <a href="https://www.youtube.com/@JerseyMindsetAuntie" target="_blank" rel="noopener noreferrer">
                <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                  Watch on YouTube <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 hover:border-purple-600/40 transition-all">
              <Headphones className="w-10 h-10 text-purple-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Corporate Licensing</h3>
              <p className="text-gray-400 mb-4">Bring the Mindset Shift experience to your organization. Custom packages for corporate training and events.</p>
              <Button onClick={() => navigate('/start-project')} className="bg-purple-600 hover:bg-purple-700 text-white">
                Inquire <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Get the Full eCoSystem</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">Access all Mindset Shift media and content in one comprehensive bundle.</p>
          <Button onClick={() => navigate('/mindset-shift-ecosystem')} className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-4 text-lg">
            View Complete Bundle — $97 <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MindsetShiftLandingPage;
